import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  model,
  OnInit,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ClickOutside } from 'ngxtension/click-outside';
import {
  SelectGroupModal,
  SelectGroupModalProps,
} from './select-group-modal/select-group.modal';
import { SelectGroupSelectedItemComponent } from './select-group-selected-item/select-group-selected-item.component';
import { SelectGroupWrapperComponent } from './select-group-wrapper/select-group-wrapper.component';
import { Input } from '../../input/input.component';
import { Label } from '../../label/label.component';
import { Button } from '../../button/button.component';
import { SelectStore } from '../select.store';
import { controlValueAccessorProvider } from '../../common/value-accessor';
import { SelectAdapter } from '../select.adapter';
import { ModalService } from 'libs/front-share/src/services/service';
import { groupByArray } from 'libs/front-share/src/helpers/group-by';
import { groupBy as _groupBy } from '../../../helpers/group-by';
import { CdkMenuModule } from '@angular/cdk/menu';
import { Menu } from '../../menu/menu.component';

export type SelectGroupItem = {
  group: string;
  options: any[];
};

@Component({
  selector: 'app-select-group',
  templateUrl: './select-group.component.html',
  imports: [
    CommonModule,
    Input,
    Label,
    SelectGroupWrapperComponent,
    SelectGroupSelectedItemComponent,
    ClickOutside,
    Button,
    CdkMenuModule,
    Menu,
  ],
  providers: [SelectStore, controlValueAccessorProvider(SelectGroupComponent)],
  styleUrls: ['./select-group.component.scss'],
})
export class SelectGroupComponent
  extends SelectAdapter<string[] | string>
  implements OnInit
{
  readonly modalService = inject(ModalService);

  query = model<string>('');

  /**
   * 그룹핑할 변수명
   */
  groupBy = input<string>();

  /**
   * 그룹핑할 변수가 array일 경우 사용
   */
  groupByArray = input<string>();

  multiple = input<boolean>(true);

  /**
   * 그룹화된 옵션 배열
   */
  groupArray = computed<SelectGroupItem[]>(() => {
    const base = [{ group: '전체', options: this.options() }];
    const groupBy = this.groupBy();
    const groupArray = this.groupByArray();
    if (!this.options() || !this.options().length) return base;
    if (!groupBy && !groupArray) return base;

    if (groupArray && groupBy) {
      return [...base, ...groupByArray(this.options(), groupArray, groupBy)];
    }

    return [...base, ..._groupBy(this.options(), groupBy)];
  });
  groupArray$ = toObservable(this.groupArray);

  currentArray = computed(() => {
    const query = this.query();
    const array = this.groupArray();

    if (!query) return array;
    return array.map((item) => {
      const options = item.options.filter((option) => {
        return option[this.bindLabel() || '']
          .toLowerCase()
          .includes(query.toLowerCase());
      });
      return { ...item, options };
    });
  });

  override handleOpen(ev?: any) {
    if (ev) {
      ev.stopPropagation();
      ev.preventDefault();
    }

    if (this.isMobile()) {
      const modal = this.modalService.create<SelectGroupModalProps>(
        SelectGroupModal,
        {
          componentProps: {
            label: this.label() || '',
            groupArray: this.groupArray(),
            bindLabel: this.bindLabel() || '',
            bindValue: this.bindValue() || '',
            initialValue: this.value(),
          },
        },
      );
      modal.closed.subscribe({
        next: (res: string[]) => {
          if (res) {
            this.value.set(res);
          }
        },
      });
    } else {
      this.open.set(!this.open());
    }
  }

  handleRemove(value: string) {
    if (this.multiple()) {
      this.value.set((this.value() as any[])?.filter((v) => v !== value));
    } else {
      this.value.set('');
    }
  }
}
